<template>
  <section class="pricing section" id="pricing">
    <div class="pricing-container">
      <div class="pricing-header">
        <span class="section-badge">{{ $t('pricing.badge') }}</span>
        <h2 class="section-title">{{ $t('pricing.title') }}</h2>
        <p class="section-subtitle">{{ $t('pricing.subtitle') }}</p>
      </div>

      <div class="pricing-toggle">
        <span :class="{ active: !isAnnual }">{{ $t('pricing.monthly') }}</span>
        <button class="toggle-switch" @click="isAnnual = !isAnnual">
          <span class="toggle-slider" :class="{ annual: isAnnual }"></span>
        </button>
        <span :class="{ active: isAnnual }">
          {{ $t('pricing.annual') }}
          <span class="discount-badge">-20%</span>
        </span>
      </div>

      <div class="pricing-cards">
        <div 
          v-for="(tier, index) in tiers" 
          :key="tier.id"
          class="pricing-card"
          :class="{ 
            'featured': tier.featured,
            'tier-basic': index === 0,
            'tier-advanced': index === 1,
            'tier-premium': index === 2
          }"
        >
          <div v-if="tier.featured" class="popular-badge">
            {{ $t('pricing.popular') }}
          </div>

          <div class="tier-header">
            <div class="tier-icon">{{ tier.icon }}</div>
            <h3 class="tier-name">{{ tier.name }}</h3>
            <p class="tier-tagline">{{ tier.tagline }}</p>
          </div>

          <div class="tier-pricing">
            <div class="price">
              <span class="currency">$</span>
              <span class="amount">{{ isAnnual ? tier.priceAnnual : tier.price }}</span>
              <span class="period">USD/{{ $t('pricing.month') }}</span>
            </div>
            <div v-if="isAnnual" class="annual-savings">
              {{ $t('pricing.save') }} ${{ tier.price * 12 - tier.priceAnnual * 12 }}/{{ $t('pricing.year') }}
            </div>
          </div>

          <div class="cashback-highlight">
            <div class="cashback-percentage">
              <span class="big-number">{{ tier.cashback }}%</span>
              <span class="cashback-label">Cashback</span>
            </div>
            <div class="cashback-details">
              <span class="limit-text">{{ $t('pricing.upto') }}</span>
              <span class="limit-amount">{{ formatCOP(tier.cashbackLimit) }}</span>
              <span class="monthly-return">{{ $t('pricing.maxReturn') }}: {{ formatCOP(tier.maxReturn) }}</span>
            </div>
          </div>

          <button 
            class="tier-cta"
            :class="{ primary: tier.featured }"
            @click="selectTier(tier)"
          >
            {{ tier.featured ? $t('pricing.ctaPrimary') : $t('pricing.ctaSecondary') }}
          </button>

          <div class="tier-features">
            <div class="features-title">{{ $t('pricing.included') }}:</div>
            <ul class="features-list">
              <li v-for="feature in tier.features" :key="feature" class="feature-item">
                <svg class="check-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16.667 5L7.5 14.167L3.333 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ feature }}</span>
              </li>
            </ul>
            <div v-if="tier.exclusive && tier.exclusive.length > 0" class="exclusive-features">
              <div class="features-title">{{ $t('pricing.exclusive') }}:</div>
              <ul class="features-list">
                <li v-for="feature in tier.exclusive" :key="feature" class="feature-item exclusive">
                  <svg class="star-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2L12.09 7.26L18 8.27L14 12.14L15.18 18L10 15.27L4.82 18L6 12.14L2 8.27L7.91 7.26L10 2Z" fill="currentColor"/>
                  </svg>
                  <span>{{ feature }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="pricing-footer">
        <p class="guarantee">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" class="shield-icon">
            <path d="M12 2L4 7V12C4 16.5 7 20.74 12 22C17 20.74 20 16.5 20 12V7L12 2Z" stroke="currentColor" stroke-width="2"/>
            <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ $t('pricing.guarantee') }}
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import sr from '@/scrollreveal.js';

export default {
  name: 'PricingSection',
  data() {
    return {
      isAnnual: false,
    };
  },
  computed: {
    tiers() {
      return [
        {
          id: 'basic',
          name: this.$t('pricing.tiers.basic.name'),
          tagline: this.$t('pricing.tiers.basic.tagline'),
          icon: '🟢',
          price: 5,
          priceAnnual: 4,
          cashback: 4,
          cashbackLimit: 200000,
          maxReturn: 8000,
          featured: false,
          features: [
            this.$t('pricing.tiers.basic.features.f1'),
            this.$t('pricing.tiers.basic.features.f2'),
            this.$t('pricing.tiers.basic.features.f3'),
            this.$t('pricing.tiers.basic.features.f4')
          ],
          exclusive: []
        },
        {
          id: 'advanced',
          name: this.$t('pricing.tiers.advanced.name'),
          tagline: this.$t('pricing.tiers.advanced.tagline'),
          icon: '🔵',
          price: 10,
          priceAnnual: 8,
          cashback: 6,
          cashbackLimit: 500000,
          maxReturn: 30000,
          featured: true,
          features: [
            this.$t('pricing.tiers.advanced.features.f1'),
            this.$t('pricing.tiers.advanced.features.f2'),
            this.$t('pricing.tiers.advanced.features.f3'),
            this.$t('pricing.tiers.advanced.features.f4')
          ],
          exclusive: [
            this.$t('pricing.tiers.advanced.exclusive.e1')
          ]
        },
        {
          id: 'premium',
          name: this.$t('pricing.tiers.premium.name'),
          tagline: this.$t('pricing.tiers.premium.tagline'),
          icon: '🟣',
          price: 15,
          priceAnnual: 12,
          cashback: 8,
          cashbackLimit: 1000000,
          maxReturn: 80000,
          featured: false,
          features: [
            this.$t('pricing.tiers.premium.features.f1'),
            this.$t('pricing.tiers.premium.features.f2'),
            this.$t('pricing.tiers.premium.features.f3'),
            this.$t('pricing.tiers.premium.features.f4')
          ],
          exclusive: [
            this.$t('pricing.tiers.premium.exclusive.e1'),
            this.$t('pricing.tiers.premium.exclusive.e2'),
            this.$t('pricing.tiers.premium.exclusive.e3')
          ]
        }
      ];
    }
  },
  mounted() {
    sr.reveal('.pricing-header', { delay: 100 });
    sr.reveal('.pricing-toggle', { delay: 200 });
    sr.reveal('.pricing-card', { interval: 100, delay: 300 });
    sr.reveal('.pricing-footer', { delay: 500 });
  },
  methods: {
    formatCOP(amount) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0
      }).format(amount);
    },
    selectTier(tier) {
      const target = document.querySelector('#contact');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
        localStorage.setItem('selectedTier', tier.id);
      }
    }
  }
};
</script>


<style src="@/styles/Subscription-Section.css"></style>